/**
 * On-thread TypeScript environment for the Script Widget editor (Phase L4b).
 *
 * The TS package is ~6 MB unminified and would bloat the main app bundle, so
 * everything heavy is dynamically imported — `getTsKit()` only fetches when
 * the Script editor actually mounts. Vite splits this into its own chunk.
 *
 * One shared `VirtualTypeScriptEnvironment` per session — every ScriptEditor
 * instance creates its own pair of files inside it:
 *   /script-sdk.d.ts        (ambient types — written once at boot)
 *   /header-<id>.d.ts       (per-category `declare const ctx: HomeContext`)
 *   /script-<id>.ts         (the user's code, kept in sync via tsSync)
 *
 * We bootstrap from `createDefaultMapFromCDN` so we don't bundle several MB
 * of lib.*.d.ts files. The CDN call hits unpkg once per (TS version × lib
 * set) and caches the result in localStorage.
 */

let kitPromise = null;
let editorSeq = 0;

export function nextEditorId() {
  return ++editorSeq;
}

const CTX_TYPE_FOR_CATEGORY = {
  home: 'HomeContext',
  case: 'CaseContext',
  task: 'TaskContext',
  add_case: 'AddCaseContext',
  add_task: 'AddTaskContext',
};

export function headerForCategory(category) {
  const type = CTX_TYPE_FOR_CATEGORY[category] || 'ScriptContext';
  return `// Generated header — Script Widget editor stamp.\ndeclare const ctx: ${type};\n`;
}

export function pathsForEditor(id) {
  return {
    headerPath: `/header-${id}.d.ts`,
    scriptPath: `/script-${id}.ts`,
  };
}

/**
 * Resolve once per session — subsequent calls share the env + valtown module.
 * Caller receives:
 *   { env, valtown } where valtown re-exports tsFacet/tsSync/tsAutocomplete/tsLinter/tsHover.
 */
export function getTsKit() {
  if (kitPromise) return kitPromise;
  kitPromise = (async () => {
    const [tsModule, vfsModule, valtownModule, sdkModule] = await Promise.all([
      import('typescript'),
      import('@typescript/vfs'),
      import('@valtown/codemirror-ts'),
      import('./script-sdk.d.ts?raw'),
    ]);
    const ts = tsModule.default || tsModule;
    const { createDefaultMapFromCDN, createSystem, createVirtualTypeScriptEnvironment } = vfsModule;
    const scriptSdkDts = sdkModule.default;

    const COMPILER_OPTIONS = {
      target: ts.ScriptTarget.ES2022,
      module: ts.ModuleKind.ESNext,
      allowJs: true,
      checkJs: false,
      noEmit: true,
      strict: false,
      skipLibCheck: true,
      lib: ['ES2022', 'DOM'],
    };

    const fsMap = await createDefaultMapFromCDN(
      COMPILER_OPTIONS,
      ts.version,
      true,
      ts,
    );
    fsMap.set('/script-sdk.d.ts', scriptSdkDts);
    const sys = createSystem(fsMap);
    const env = createVirtualTypeScriptEnvironment(sys, ['/script-sdk.d.ts'], ts, COMPILER_OPTIONS);
    return { env, valtown: valtownModule };
  })().catch((err) => {
    if (typeof console !== 'undefined') console.warn('[tsEnv] failed to bootstrap:', err);
    kitPromise = null;
    throw err;
  });
  return kitPromise;
}
